import React, { useMemo } from 'react'
import type { EChartsOption } from 'echarts'
import ReactECharts from 'echarts-for-react'
import { DcChartProps } from './dc-chart-types'
import { ButtonsGroup } from '../../common-styled-component/section'
import { SectionContent } from '../../common-styled-component/section'
import { DcButton } from '../../dc-button/dc-button'
import { DcDiv } from '../../dc-div/dc-div'
import { DcFlexBox } from '../../dc-flex-box/dc-flex-box'
import { DcText } from '../../dc-text/dc-text'
import { PixelSpinner } from '../../pixel-spinner/pixel-spinner'
import { $DCprimaryColor } from '../../styleGuide'
import { colors } from '../../styleGuide'

const DcChart = ({
  type,
  data,
  height = '350px',
  width = '100%',
  className = '',
  options = {},
  title,
  headerData,
  toggleData,
  isTitleClickable = false,
  onClickTitle,
  isLoading,
  onToggleDataClick,
  activeToggle,
  showLabelsInside = false,
}: DcChartProps) => {
  const chartOptions = useMemo(() => {
    const baseOptions: EChartsOption = {
      tooltip: {
        trigger: 'axis' as const,
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
      },
      ...options,
    }

    switch (type) {
      case 'line':
        return {
          ...baseOptions,
          xAxis: {
            type: 'category' as const,
            data: data.xAxis || [],
          },
          yAxis: {
            type: 'value' as const,
          },
          series:
            data.series?.map((series: any) => ({
              name: series.name,
              type: 'line' as const,
              data: series.data,
              showSymbol: series.showSymbol || false,
              ...(series.itemStyle && { itemStyle: series.itemStyle }),
              ...(series.lineStyle && { lineStyle: series.lineStyle }),
            })) || [],
        }

      case 'area':
        return {
          ...baseOptions,
          xAxis: {
            type: 'category' as const,
            boundaryGap: false,
            data: data.xAxis || [],
          },
          yAxis: {
            type: 'value' as const,
          },
          series:
            data.series?.map((series: any) => ({
              name: series.name,
              type: 'line' as const,
              areaStyle: {},
              showSymbol: series.showSymbol || false,
              data: series.data,
              ...(series.itemStyle && { itemStyle: series.itemStyle }),
              ...(series.areaStyle && { areaStyle: series.areaStyle }),
            })) || [],
        }

      case 'bar':
        return {
          ...baseOptions,
          grid: {
            ...baseOptions.grid,
            left: 20, // Reduced left margin for full width
            right: 20, // Reduced right margin for full width
          },
          xAxis: {
            type: 'category' as const,
            data: data.xAxis || [],
            axisLabel: {
              show: false, // Hide X-axis labels for regular bars
            },
          },
          yAxis: {
            type: 'value' as const,
          },
          series:
            data.series?.map((series: any) => ({
              name: series.name,
              type: 'bar' as const,
              data: series.data,
              ...(series.itemStyle && { itemStyle: series.itemStyle }),
              label: series.label || {
                show: true,
                position: 'top',
                formatter: (params: any) => {
                  return params.value || ''
                },
                color: colors.white,
                fontSize: 12,
                fontWeight: 'bold',
              },
            })) || [],
        }

      case 'stackedBar':
        return {
          ...baseOptions,
          grid: {
            ...baseOptions.grid,
            left: 20, // Reduced left margin for full width
            right: 20, // Reduced right margin for full width
          },
          xAxis: {
            type: 'category' as const,
            data: data.xAxis || [],
            axisLabel: {
              show: false, // Hide X-axis labels for stacked bars
            },
          },
          yAxis: {
            type: 'value' as const,
          },
          series:
            data.series?.map((series: any) => ({
              name: series.name,
              type: 'bar' as const,
              stack: series.stack || data.stackName || 'total',
              emphasis: {
                focus: 'series',
              },
              data: series.data,
              ...(series.itemStyle && { itemStyle: series.itemStyle }),
              label: series.label || {
                show: true,
                position: 'top',
                formatter: (params: any) => {
                  return params.value || ''
                },
                color: colors.white,
                fontSize: 12,
                fontWeight: 'bold',
              },
            })) || [],
        }

      case 'mixedLineBar':
        return {
          ...baseOptions,
          legend: {
            ...(baseOptions.legend || {}),
          },
          xAxis: {
            type: 'category' as const,
            data: data.xAxis || [],
          },
          yAxis: data.dualYAxis
            ? [
                {
                  type: 'value' as const,
                  name: data.yAxisNames?.[0] || '',
                  ...(data.yAxisOptions?.[0] || {}),
                },
                {
                  type: 'value' as const,
                  name: data.yAxisNames?.[1] || '',
                  ...(data.yAxisOptions?.[1] || {}),
                },
              ]
            : {
                type: 'value' as const,
              },
          series:
            data.series?.map((series: any) => ({
              name: series.name,
              type: series.type || 'bar',
              ...(series.yAxisIndex !== undefined && {
                yAxisIndex: series.yAxisIndex,
              }),
              data: series.data,
              ...(series.itemStyle && { itemStyle: series.itemStyle }),
              ...(series.lineStyle && { lineStyle: series.lineStyle }),
              ...(series.symbolSize !== undefined && {
                symbolSize: series.symbolSize,
              }),
              ...(series.smooth !== undefined && { smooth: series.smooth }),
            })) || [],
        }

      case 'horizontalBar':
        return {
          ...baseOptions,
          grid: {
            ...baseOptions.grid,
            left: 20, // Reduced left margin for full width
            right: 20, // Reduced right margin for full width
          },
          xAxis: {
            type: 'value' as const,
          },
          yAxis: {
            type: 'category' as const,
            data: data.yAxis || [],
            axisLabel: {
              show: false, // Always hide Y-axis labels for horizontal bars
            },
          },
          series:
            data.series?.map((series: any) => ({
              name: series.name,
              type: 'bar' as const,
              stack: series.stack || data.stackName || undefined,
              data: series.data,
              ...(series.itemStyle && { itemStyle: series.itemStyle }),
              label: series.label || {
                show: true,
                position: 'insideLeft',
                formatter: (params: any) => {
                  return data.yAxis?.[params.dataIndex] || ''
                },
                color: colors.white,
                fontSize: 12,
                fontWeight: 'bold',
                padding: [4, 8],
                width: 350,
                overflow: 'truncate',
                },
            })) || [],
        }

      case 'pie':
        return {
          ...baseOptions,
          tooltip: {
            trigger: 'item' as const,
            formatter: '{a} <br/>{b}: {c} ({d}%)',
          },
          series: [
            {
              name: data.name || 'Data',
              type: 'pie' as const,
              radius: data.radius || '50%',
              center: data.center || ['50%', '50%'],
              data: data.series || [],
              emphasis: {
                itemStyle: {
                  shadowBlur: 10,
                  shadowOffsetX: 0,
                  shadowColor: 'rgba(0, 0, 0, 0.5)',
                },
              },
              ...(data.itemStyle && { itemStyle: data.itemStyle }),
              ...(data.label && { label: data.label }),
            },
          ],
        }

      case 'heatmap':
        return {
          ...baseOptions,
          tooltip: {
            position: 'top',
            trigger: 'item' as const,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderColor: 'rgba(0, 0, 0, 0.1)',
            borderWidth: 1,
            extraCssText:
              'box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) border-radius: 8px padding: 12px 16px backdrop-filter: blur(4px)',
            textStyle: {
              color: colors.white,
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 14,
            },
            formatter:
              data.customTooltip ||
              ((params: any) => {
                const value = params.data[2]
                const day = data.xAxis[params.data[0]]
                const hour = data.yAxis[params.data[1]]

                // Calculate activity level
                let activityLevel = 'Low'
                const percentage = (value / data.max) * 100
                if (percentage > 70) activityLevel = 'High'
                else if (percentage > 30) activityLevel = 'Medium'

                // Choose color based on activity level
                let activityColor = colors.green // Green for high
                if (activityLevel === 'Medium') activityColor = colors.orange // Orange for medium
                if (activityLevel === 'Low') activityColor = colors.blue // Blue for low

                return `
                  <div style='font-weight: 600 margin-bottom: 8px font-size: 15px display: flex align-items: center gap: 6px'>
                    <span style='display: inline-block width: 10px height: 10px border-radius: 50% background-color: ${activityColor}'></span>
                    ${day}, ${hour}
                  </div>
                  <div style='display: flex justify-content: space-between margin-bottom: 4px'>
                    <span style='color: ${colors.grayTextDark}'>Value:</span>
                    <span style='font-weight: 600'>${value}</span>
                  </div>
                  <div style='display: flex justify-content: space-between'>
                    <span style='color: ${colors.grayTextDark}'>Activity:</span>
                    <span style='font-weight: 500 color: ${activityColor}'>${activityLevel}</span>
                  </div>
                `
              }),
            ...data.tooltipOptions,
          },
          xAxis: {
            type: 'category' as const,
            data: data.xAxis || [],
            splitArea: {
              show: true,
            },
          },
          yAxis: {
            type: 'category' as const,
            data: data.yAxis || [],
            splitArea: {
              show: true,
            },
          },
          visualMap: {
            min: data.min || 0,
            max: data.max || 10,
            calculable: true,
            orient: 'horizontal',
            left: 'center',
            bottom: '15%',
            ...data.visualMap,
          },
          series: [
            {
              name: data.name || 'Heatmap',
              type: 'heatmap' as const,
              data: data.series || [],
              label: {
                show: data.showLabel || true,
              },
              emphasis: {
                itemStyle: {
                  shadowBlur: 10,
                  shadowColor: 'rgba(0, 0, 0, 0.5)',
                },
              },
              ...(data.itemStyle && { itemStyle: data.itemStyle }),
            },
          ],
        }

      default:
        return baseOptions
    }
  }, [type, data, options, showLabelsInside])

  return (
    <SectionContent
      style={{ padding: 0, position: 'relative' }}
      className="dc-chart-container"
    >
      <PixelSpinner isShow={isLoading || false} />

      <div style={{ marginBottom: '15px', padding: '10px 20px' }}>
        <DcFlexBox justifyContent="space-between">
          <DcDiv
            onClick={() => onClickTitle && onClickTitle()}
            hoverStyle={
              isTitleClickable
                ? {
                    color: $DCprimaryColor,
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }
                : {}
            }
            style={{
              fontSize: '20px',
              cursor: isTitleClickable ? 'pointer' : 'default',
            }}
          >
            {title}
          </DcDiv>
          {headerData && (
            <>
              &nbsp;&nbsp;
              <DcFlexBox height="auto" justifyContent="flex-end">
                {' '}
                <DcText style={{ fontSize: '14px' }}>
                  {headerData.title} :
                </DcText>
                &nbsp;
                <DcText style={{ fontSize: '14px' }}>{headerData.data}</DcText>
              </DcFlexBox>
            </>
          )}
          {toggleData && (
            <>
              <ButtonsGroup>
                <DcButton
                  size="sm"
                  onClick={() => onToggleDataClick && onToggleDataClick(0)}
                  variant={activeToggle === 0 ? 'primary' : 'outline-primary'}
                  active={activeToggle === 0}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: 'bold', fontSize: '12px' }}>
                      {toggleData[0].title}
                    </div>
                    <div style={{ fontSize: '11px', marginTop: '2px' }}>
                      {toggleData[0].data}
                    </div>
                  </div>
                </DcButton>
                <DcButton
                  size="sm"
                  onClick={() => onToggleDataClick && onToggleDataClick(1)}
                  variant={activeToggle === 1 ? 'warning' : 'outline-warning'}
                  active={activeToggle === 1}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: 'bold', fontSize: '12px' }}>
                      {toggleData[1].title}
                    </div>
                    <div style={{ fontSize: '11px', marginTop: '2px' }}>
                      {toggleData[1].data}
                    </div>
                  </div>
                </DcButton>
              </ButtonsGroup>
            </>
          )}
        </DcFlexBox>
      </div>
      {data?.series && data?.series?.length > 0 && !isLoading ? (
        <ReactECharts
          option={chartOptions}
          style={{ height, width }}
          className={className}
          opts={{ renderer: 'canvas' }}
        />
      ) : (
        <DcFlexBox
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '350px',
            width: '100%',
          }}
        >
          <DcText>No data to display...</DcText>
        </DcFlexBox>
      )}
    </SectionContent>
  )
}

export default DcChart
